import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService, UserProfile } from '../../features/authentication/services/auth.service';
import { StorageService } from './storage.service';
import { STORAGE_KEYS, UserRole } from '../constants/app-constants';
import { API_ENDPOINTS } from '../constants/api-endpoints';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private authService = inject(AuthService);
  private storage = inject(StorageService);
  private http = inject(HttpClient);

  readonly profile = signal<UserProfile | null>(null);
  readonly isSubscriptionActive = signal<boolean>(
    this.storage.get(STORAGE_KEYS.SUBSCRIPTION_ACTIVE) !== 'false'
  );

  setSubscriptionActive(value: boolean): void {
    this.isSubscriptionActive.set(value);
    this.storage.set(STORAGE_KEYS.SUBSCRIPTION_ACTIVE, String(value));
  }

  loadProfile(): void {
    this.authService.getProfile().subscribe({
      next: (res) => {
        this.profile.set(res.data);

        // If the user is staff, store/refresh permissions from the profile
        if (res.data.role === UserRole.STAFF && (res.data as any).permissions) {
          this.storage.setJson(STORAGE_KEYS.STAFF_PERMISSIONS, (res.data as any).permissions);
        }
      },
    });
  }

  checkSubscriptionStatus(): void {
    this.http
      .get<{ success: boolean; message: string; data: { isSubscriptionActive: boolean } }>(
        API_ENDPOINTS.pharmacyVendor.subscription
      )
      .subscribe({
        next: (res) => this.setSubscriptionActive(res.data.isSubscriptionActive),
      });
  }

  /** Get staff permissions from localStorage */
  getStaffPermissions(): Record<string, boolean> {
    return this.storage.getJson<Record<string, boolean>>(STORAGE_KEYS.STAFF_PERMISSIONS) ?? {};
  }

  /** Check if current user has a specific staff permission */
  hasPermission(permissionKey: string): boolean {
    const role = this.storage.get(STORAGE_KEYS.USER_ROLE);
    if (role === UserRole.SUPER_ADMIN) return true; // Super admin has all permissions
    if (role !== UserRole.STAFF) return false;
    const permissions = this.getStaffPermissions();
    return permissions[permissionKey] === true;
  }

  /** Check if user is admin-level (super_admin or staff) */
  isAdminLevel(): boolean {
    const role = this.storage.get(STORAGE_KEYS.USER_ROLE);
    return role === UserRole.SUPER_ADMIN || role === UserRole.STAFF;
  }

  clearProfile(): void {
    this.profile.set(null);
  }
}
